/** How a draft dialog (preview / restore summary) renders the warehouse field for a feature. */
export type WarehouseDisplay = 'none' | 'single' | 'fromTo';

/** Per-feature display config for the draft preview and restore-summary dialogs. */
export interface DraftPreviewConfig {
  showPrice: boolean;
  warehouse: WarehouseDisplay;
}

/** Permissive fallback for legacy/unknown draft keys: show everything we have. */
export const DEFAULT_PREVIEW_CONFIG: DraftPreviewConfig = { showPrice: true, warehouse: 'single' };

export interface DraftFeature {
  id: 'setInsert' | 'transfer' | 'unload';
  prefix: string;
  routeMatches(url: string): boolean;
  /** Rows that belong in the draft for this feature (completed, valid). */
  isValidRow(row: any): boolean;
  /** Which optional fields the preview / restore-summary dialogs show for this feature. */
  preview: DraftPreviewConfig;
}

export const SET_INSERT_FEATURE: DraftFeature = {
  id: 'setInsert',
  prefix: 'si:',
  routeMatches: (url) => url.includes('set-insert-out'),
  // set-insert validity remains owned by TableEditRowsComponent (getValid/filterFN).
  isValidRow: () => true,
  preview: { showPrice: true, warehouse: 'single' },
};

const validMovementRow = (row: any): boolean =>
  !!row && row.instanceValue > 0 && row.instanceValue <= row.avaible;

export const TRANSFER_FEATURE: DraftFeature = {
  id: 'transfer',
  prefix: 'tr:',
  routeMatches: (url) => /\/movement\/TRANS(\/|$|\?)/.test(url),
  isValidRow: (row) =>
    validMovementRow(row) &&
    !!row.instanceValueWarehouse?.id && !!row.instanceValueWarehouse?.name &&
    row.instanceValueWarehouse?.id !== row.warehouse?.id,
  preview: { showPrice: false, warehouse: 'fromTo' },
};

export const UNLOAD_FEATURE: DraftFeature = {
  id: 'unload',
  prefix: 'un:',
  routeMatches: (url) => /\/movement\/OUT(\/|$|\?)/.test(url),
  isValidRow: (row) => validMovementRow(row),
  // Unload has no destination; show the source warehouse (the stock's own warehouse).
  preview: { showPrice: false, warehouse: 'single' },
};

export const DRAFT_FEATURES: DraftFeature[] = [
  SET_INSERT_FEATURE, TRANSFER_FEATURE, UNLOAD_FEATURE,
];

/**
 * Names of every property on `source` whose value is boolean true — used to surface
 * card flags (foil, signed, stamped, ...) without enumerating them, so a new flag
 * added on the backend shows up automatically. Non-boolean fields (ids, names, nested
 * objects like `condition`/`editable`) are ignored.
 */
export function collectFlags(source: any): string[] {
  return Object.keys(source ?? {}).filter(k => source[k] === true);
}

export function resolveActiveFeature(url: string): DraftFeature | null {
  if (!url) { return null; }
  return DRAFT_FEATURES.find(f => f.routeMatches(url)) ?? null;
}

/** Resolve the owning feature from a draft key's prefix (si:/tr:/un:), or null for a
 *  legacy/unknown key. */
export function featureForKey(key: string): DraftFeature | null {
  return DRAFT_FEATURES.find(f => key?.startsWith(f.prefix)) ?? null;
}

/** Resolve the preview/summary display config from a draft key's prefix (si:/tr:/un:). */
export function previewConfigForKey(key: string): DraftPreviewConfig {
  return featureForKey(key)?.preview ?? DEFAULT_PREVIEW_CONFIG;
}
